import React, { useEffect, useRef, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import zhTwLocale from '@fullcalendar/core/locales/zh-tw';

import { extendedProps as EP_FIELDS, recordExtendedProps, staffOrderExtendedProps, OrderInfoCardList, RecordsInfoCardList, StaffOrderInfoCardList } from '../../../constants';
import '../../../css/components/Schedule/Calendar/Calendar.css';

import { GenericModal } from '../../../components';
import { formatSecondsToHMS, formatTaiwanDateTime, formatTaiwanDateTimeAMPM, OrderId } from '../../../utils';

import { getDeliverRecordByDeliver } from '../../../service/api/DeliveryRecord/apiDeliveryRecord';
import { getOrders } from '../../../service/api/Order/apiOrder';
import { getDeliverOrders } from '../../../service/api/StaffOrder/apiStaffOrder';
import { getCompanies } from '../../../service/api/Company/apiCompany'

const Calendar = () => {

  const calendarRef = useRef(null);
  const [viewType, setViewType] = useState('isShipped'); // 'isShipped' 或 'status'
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [ordersData, setOrdersData] = useState([]);
  const [deliverRecords, setDeliverRecords] = useState([]);
  const [staffOrder, setStaffOrder] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null); // 存放點擊事件
  const [isModalOpen, setIsModalOpen] = useState(false);
  const calendarEvents = (
    viewType === 'record' ? deliverRecords :
      viewType === 'staffOrder' ? (
        // 如果有選公司，就過濾 staffOrder
        selectedCompany ? staffOrder.filter(item => String(item.companyId) === String(selectedCompany)) : staffOrder
      ) : ordersData
  ).map(data => {
    const extendedProps = {};
    (viewType === 'record' ? recordExtendedProps : viewType === 'staffOrder' ? staffOrderExtendedProps : EP_FIELDS).forEach(field => {
      if (field === 'address') {
        extendedProps[field] = `${data.addressCity}${data.addressDistrict}${data.addressDetailed}`;
      } else {
        extendedProps[field] = data[field];
      }
    });
    if (viewType === 'isShipped') {

      return {
        id: OrderId(data.id),
        title: `${data.name} - ${data.isShipped ? "已送達" : "未送達"}`,
        deliveryDate: data.deliveryDate.slice(0, 10),
        start: data.deliveryDate,
        ...extendedProps,
        color: data.isShipped ? "#28a745" : "#ffc107"
      }
    } else if (viewType === 'record') {
      return {
        id: OrderId(data.id),
        title: `${data.deliverName} - ${data.status ? "已完成" : "待完成"}`,
        deliveryDate: data.deliveryDate.slice(0, 10),
        start: data.deliveryDate,
        ...extendedProps,
        color: data.status ? "#4fc107" : "#c911d2"
      }
    } else if (viewType === 'staffOrder') {
      return {
        id: OrderId(data.id),
        title: `${data.name} - ${data.plate}`,
        // usedAt: data.usedAt.slice(0, 10),
        start: formatTaiwanDateTime(data.usedAt),
        ...extendedProps,
        color: data.status === 1 ? data.isReturned === "未歸還" ? "#ebb521" : "#00a738" : data.status === 2 ? "#5e5e5e" : "#ff0800"
      }
    }
    else {
      return {
        id: OrderId(data.id),
        title: `${data.name} - ${data.status === 1 ? "已通過" : data.status === 2 ? "未通過" : "未審核"}`,
        deliveryDate: data.deliveryDate.slice(0, 10),
        start: data.deliveryDate,
        ...extendedProps,
        color: data.status ? data.status === 2 ? "#3e59f5" : "#0dcaf0" : "#dc3545"
      }
    }
  });

  useEffect(() => {
    if (viewType !== 'staffOrder') setSelectedCompany('');

    if (viewType === 'record') {
      getDeliverRecordByDeliver({ type: 'all' })
        .then(response => {
          if (response?.status === 200) {
            console.log(response)
            setDeliverRecords(response.data.result);
          }
        });
    }

    if (viewType === 'staffOrder') {
      getDeliverOrders()
        .then(response => {
          if (response?.status === 200) {
            console.log(response)
            setStaffOrder(response.data.result);
          }
        });

      getCompanies()
        .then(response => {
          if (response?.status === 200) {
            console.log(response);
            setCompanies(response.data.result);
          }
        })

        setSelectedCompany(1)
    }

    if (viewType === 'isShipped' || viewType === 'status') {
      getOrders()
        .then(response => {
          if (response?.status === 200) {
            console.log(response)
            setOrdersData(response.data.result)
          }
        });
    }

  }, [viewType]);

  return (
    <div className="container-fluid">
      <h1 className="h3 mb-4 mt-4 text-gray-800 text-primary">訂單日曆</h1>

      <div className="card shadow mb-4">
        <div className="card-header py-3 d-flex align-items-center">
          <h6 className="m-0 fw-bold text-primary">
            <i className="fas fa-calendar-alt me-2"></i>
            查看送貨日期
          </h6>
        </div>

        <div className="card-body">
          <div className="CalendarLayout">
            <div className="mb-3 flex-wrap d-flex justify-content-center">
              <button className={viewType === 'isShipped' ? "btn btn-primary me-2" : "btn btn-secondary me-2"} onClick={() => setViewType('isShipped')}>查看寄送狀態</button>
              <button className={viewType === 'status' ? "btn btn-primary me-2" : "btn btn-secondary me-2"} onClick={() => setViewType('status')}>查看審核狀態</button>
              <button className={viewType === 'record' ? "btn btn-primary me-2" : "btn btn-secondary me-2"} onClick={() => setViewType('record')}>查看紀錄狀態</button>
              <div className="d-flex align-items-center">
                <button className={viewType === 'staffOrder' ? "btn btn-primary me-2" : "btn btn-secondary me-2"} onClick={() => setViewType('staffOrder')}>查看派車單狀態</button>
                {/* 增加點擊後出現公司選單：僅在 staffOrder 模式下顯示 */}
                {viewType === 'staffOrder' && (
                  <select
                    className="form-select border-primary"
                    style={{ width: 'auto' }}
                    value={selectedCompany}
                    onChange={(e) => setSelectedCompany(e.target.value)}
                  >
                    <option value="">全部公司</option>
                    {companies.map(company => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>

            <FullCalendar
              key={viewType} // 可以用 viewType 來觸發重新渲染
              ref={calendarRef}
              plugins={[dayGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              locale={zhTwLocale}
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,dayGridWeek,dayGridDay'
              }}
              height="auto"
              dayMaxEvents={5}
              events={calendarEvents} // <-- 這裡直接用 calendarEvents
              eventClick={info => {
                console.log({ ...info.event._def, ...info.event.extendedProps })
                setSelectedEvent({ ...info.event._def, ...info.event.extendedProps });
                setIsModalOpen(true);
              }}
            />
          </div>
        </div>
      </div>

      {
        selectedEvent && (
          <GenericModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            children={
              viewType === 'record' ?
                <div className="card order-info-card">
                  {
                    RecordsInfoCardList.map(item => {
                      if (item.field === 'status') {

                        return <div className="mb-2">
                          <strong>{item.name}</strong>
                          <span className={`status ${selectedEvent[item.field] ? 'complete' : 'waitting'}`}>
                            {selectedEvent[item.field] ? "已完成" : "待完成"}
                          </span>
                        </div>
                      } else if (item.field === 'title') {
                        return <h5>{selectedEvent[item.field]}</h5>;
                      } else if (item.field === 'orderId') {
                        return <div className="mb-2">
                          <strong>{item.name}</strong>
                          <div style={{ display: "flex", flexWrap: "wrap", marginTop: "4px" }}>

                            {selectedEvent[item.field] ? selectedEvent[item.field].map(i => (
                              <span className="badge bg-warning text-dark me-1 mb-1" key={i}>
                                {i}
                              </span>))
                              :
                              <span className="badge bg-warning text-dark me-1 mb-1">
                              </span>
                            }
                          </div>
                        </div>
                      } else if (item.field === 'drivingTime') {
                        return <div className="mb-2"><strong>{item.name}</strong> <span>{formatSecondsToHMS(selectedEvent[item.field])}</span></div>
                      } else if (item.field === 'mapUrl') {
                        return <div className="mb-2">
                          <strong>{item.name}</strong>
                          <span>
                            {
                              selectedEvent[item.field]
                                ? (selectedEvent[item.field].length > 50
                                  ? selectedEvent[item.field].slice(0, 50) + "…"
                                  : selectedEvent[item.field])
                                : "—"
                            }
                          </span>
                        </div>
                      }
                      else {
                        return <div className="mb-2"><strong>{item.name}</strong> <span>{selectedEvent[item.field]}</span></div>
                      }
                    })
                  }
                </div>
                :
                viewType === 'staffOrder' ?
                  <div className="card order-info-card">
                    {
                      StaffOrderInfoCardList.map(item => {
                        if (item.field === 'usedAt' || item.field === 'returnAt' || item.field === 'finishAt') {

                          return <div className="mb-2">
                            <strong>{item.name}</strong>
                            <span >
                              {formatTaiwanDateTimeAMPM(selectedEvent[item.field])}
                            </span>
                          </div>
                        } else if (item.field === 'status') {
                          return <div className="mb-2">
                            <strong>{item.name}</strong>
                            <span className={`status ${selectedEvent[item.field] ? selectedEvent[item.field] === 1 ? 'approved' : 'unReviewed' : 'rejected'}`}>
                              {selectedEvent[item.field] === 1 ? "已完成" : selectedEvent[item.field] === 1 ? "已完成" : "未完成"}
                            </span>
                          </div>
                        } else if (item.field === 'title') {
                          return <h5>{selectedEvent[item.field]}</h5>
                        } else {
                          return <div className="mb-2"><strong>{item.name}</strong> <span>{selectedEvent[item.field] ? selectedEvent[item.field] : "-"}</span></div>
                        }
                      })
                    }
                  </div>
                  :
                  <div className="card order-info-card">
                    {
                      OrderInfoCardList.map(item => {
                        if (item.field === 'isShipped') {

                          return <div className="mb-2">
                            <strong>{item.name}</strong>
                            <span className={`status ${selectedEvent[item.field] ? 'shipped' : 'pending'}`}>
                              {selectedEvent[item.field] ? "已送達" : "未送達"}
                            </span>
                          </div>
                        } else if (item.field === 'status') {
                          return <div className="mb-2">
                            <strong>{item.name}</strong>
                            <span className={`status ${selectedEvent[item.field] ? selectedEvent[item.field] === 1 ? 'approved' : 'unReviewed' : 'rejected'}`}>
                              {selectedEvent[item.field] === 1 ? "已通過" : selectedEvent[item.field] === 2 ? "未通過" : "未審核"}
                            </span>
                          </div>
                        } else if (item.field === 'title') {
                          return <h5>{selectedEvent[item.field]}</h5>
                        } else {
                          return <div className="mb-2"><strong>{item.name}</strong> <span>{selectedEvent[item.field]}</span></div>
                        }
                      })
                    }
                  </div>
            }
          />

        )
      }
    </div >
  );
};

export { Calendar };
